import {
  GetStationConfig,
  GetStationConfigVariables,
  UpdateStationConfig,
  UpdateStationConfigVariables,
} from 'types/graphql'

import { Metadata, useMutation, useQuery } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import Card from 'src/components/Custom/Card/Card'
import CardSection from 'src/components/Custom/Card/CardSection'
import Checkbox from 'src/components/Custom/Checkbox/Checkbox'
import ErrorMessage from 'src/components/Custom/ErrorMessage/ErrorMessage'
import HeadingLevel from 'src/components/Custom/Heading/Heading'
import Loader from 'src/components/Custom/Loader/Loader'

import { GET_STATION_CONFIG, UPDATE_STATION_CONFIG } from './queries'

type StationConfigurationPageProps = {
  id: string
}

const StationConfigurationPage = ({ id }: StationConfigurationPageProps) => {
  const { data, loading } = useQuery<
    GetStationConfig,
    GetStationConfigVariables
  >(GET_STATION_CONFIG, {
    variables: { id },
  })

  const [updateStationConfig] = useMutation<
    UpdateStationConfig,
    UpdateStationConfigVariables
  >(UPDATE_STATION_CONFIG)

  const onStationConfigUpdate = async (
    input: UpdateStationConfigVariables['input']
  ) => {
    await toast.promise(
      updateStationConfig({
        variables: { id: data?.station?.id, input },
      }),
      {
        loading: 'Updating station config...',
        success: 'Station config updated successfully',
        error: (error) => `Error updating station config: ${error.message}`,
      }
    )
  }

  return (
    <div className="w-full bg-gray-100 p-4">
      {loading ? (
        <Loader />
      ) : !data?.station?.id ? (
        <ErrorMessage
          title="Station Not Found"
          subtitle={
            <p>
              Station with ID <code>{id}</code> not found.
            </p>
          }
        />
      ) : (
        <>
          <Metadata title="Station Configuration" />
          <HeadingLevel level={1} className="mb-4">
            Station Configuration
          </HeadingLevel>
          <Card subtitle={data?.station?.name}>
            <CardSection title="Membership status">
              <div className="mt-2 flex w-full flex-col gap-2">
                <Checkbox
                  defaultValue={data?.station?.isLocalPublicMember}
                  id="is-local-public-member"
                  onValueChange={(updatedValue) =>
                    onStationConfigUpdate({
                      isLocalPublicMember: updatedValue,
                    })
                  }
                  title="Local Public member"
                />
                <span className="text-sm text-gray-500">
                  <strong>Warning</strong>: This is <em>not</em> a sufficient
                  deactivation mechanism.
                </span>
                <Checkbox
                  defaultValue={data?.station?.isLocalPublicDemoMember}
                  id="is-local-public-demo-member"
                  onValueChange={(updatedValue) =>
                    onStationConfigUpdate({
                      isLocalPublicDemoMember: updatedValue,
                    })
                  }
                  title="Local Public demo member"
                />
                <span className="text-sm text-gray-500">
                  Ensure <strong>Local Public member</strong> is also enabled
                  for a demo station.
                </span>
              </div>
            </CardSection>
            <CardSection title="Border station status">
              <Checkbox
                defaultValue={data?.station?.borderStation}
                id="border-station"
                onValueChange={(updatedValue) =>
                  onStationConfigUpdate({ borderStation: updatedValue })
                }
                title="Border station"
              />
            </CardSection>
          </Card>
        </>
      )}
    </div>
  )
}

export default StationConfigurationPage
